import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import SearchOverlay from "./SearchOverlay";
import { SHOW_GOVERNANCE } from "@/utils/flags";

const NAV = [
  { to: "/about", label: "About" },
  { to: "/aunties", label: "The Aunties" },
  { to: "/approach", label: "Our Approach" },
  { to: "/programs", label: "Programs" },
  { to: "/threads", label: "Threads" },
  { to: "/who-we-support", label: "Who We Support" },
  { to: "/request-support", label: "Request Support" },
  { to: "/private-supports", label: "Private Supports" },
  { to: "/ndis-supports", label: "NDIS Supports" },
  { to: "/culture", label: "Culture" },
  { to: "/community", label: "Community" },
  { to: "/consultation", label: "Help Shape Ngawilyi" },
  { to: "/impact", label: "Impact" },
  { to: "/partner", label: "Partner With Us" },
  { to: "/contact", label: "Contact" },
];

const MENU_GROUPS = [
  {
    name: "About",
    links: [["Our Story / About Ngawilyi", "/about"], ["Meet Our Aunties", "/aunties"], ...(SHOW_GOVERNANCE ? [["Cultural Governance", "/cultural-governance"]] : []), ["Our Approach", "/approach"], ["Who We Support", "/who-we-support"]],
  },
  {
    name: "Support",
    links: [["Private Supports", "/private-supports"], ["NDIS Supports", "/ndis-supports"], ["Programs", "/programs"], ["Make a Referral", "/referrals"]],
  },
  {
    name: "Story & Community",
    links: [["Threads", "/threads"], ["Culture", "/culture"], ["Community", "/community"], ["Help Shape Ngawilyi", "/consultation"]],
  },
  {
    name: "Work With Us",
    links: [["Impact", "/impact"], ["Partner With Us", "/partner"], ["Contact", "/contact"]],
  },
];

const Wordmark = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
    <img src="/logo.png" alt="Ngawilyi Narrative logo" className="h-11 w-11 rounded-full shadow-[0_4px_14px_rgba(74,55,40,0.18)]" />
    <span className="flex flex-col leading-none">
      <span className="font-display text-2xl sm:text-[1.7rem] font-medium text-charcoal">
        Ngawilyi <span className="italic text-terracotta">Narrative</span>
      </span>
      <span className="text-[9px] uppercase tracking-[0.3em] text-earth/70 mt-1">Healing through story</span>
    </span>
  </Link>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-cream/90 backdrop-blur-xl shadow-[0_10px_40px_rgba(74,55,40,0.08)]" : "bg-cream/70 backdrop-blur-md"}`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-6">
        <Wordmark />
        <nav className="hidden xl:flex items-center gap-7" data-testid="desktop-nav">
          {NAV.slice(0, 6).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `text-[13px] font-semibold tracking-wide transition-colors duration-300 ${isActive ? "text-terracotta" : "text-earth hover:text-terracotta"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <NavLink to="/partner" data-testid="nav-partner-link" className={({ isActive }) => `text-[13px] font-semibold tracking-wide transition-colors duration-300 ${isActive ? "text-terracotta" : "text-earth hover:text-terracotta"}`}>
            Partner
          </NavLink>
        </nav>
        <div className="flex items-center gap-3">
          <button
            data-testid="nav-search-btn"
            onClick={() => window.dispatchEvent(new CustomEvent("open-search"))}
            aria-label="Search the site"
            className="rounded-full border border-earth/20 p-2.5 text-earth transition-all duration-300 hover:border-terracotta hover:text-terracotta hover:scale-105"
          >
            <Search className="h-5 w-5" />
          </button>
          <Link
            to="/request-support"
            data-testid="nav-request-support-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3 text-xs font-bold uppercase tracking-widest text-cream transition-all duration-300 hover:bg-charcoal hover:scale-105"
          >
            Request Support <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="xl:hidden rounded-full border border-earth/20 p-2.5 text-earth"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden bg-charcoal text-cream border-t border-earth/10"
          >
            <div className="px-6 py-6 max-h-[75vh] overflow-y-auto grain relative">
              {MENU_GROUPS.map((g, gi) => (
                <details
                  key={g.name}
                  open={gi === 0}
                  data-testid={`menu-group-${g.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group border-b border-cream/10"
                >
                  <summary className="flex items-center justify-between cursor-pointer py-4 text-[11px] uppercase tracking-[0.3em] font-bold text-ochre list-none">
                    {g.name}
                    <ChevronDown className="h-4 w-4 transition-transform duration-300 group-open:rotate-180" />
                  </summary>
                  <div className="pb-4">
                    {g.links.map(([l, to], i) => (
                      <motion.div key={to} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
                        <NavLink
                          to={to}
                          data-testid={`mobile-nav-${l.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                          className={({ isActive }) =>
                            `block py-2 font-display text-2xl ${isActive ? "text-terracotta italic" : "text-cream"}`
                          }
                        >
                          {l}
                        </NavLink>
                      </motion.div>
                    ))}
                  </div>
                </details>
              ))}
              <Link
                to="/request-support"
                data-testid="mobile-menu-request-support-btn"
                className="mt-6 mb-2 flex items-center justify-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 active:scale-95"
              >
                Request Support <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="relative bg-charcoal text-cream overflow-hidden grain">
    <div className="absolute -top-32 right-0 w-96 h-96 bg-terracotta/25 organic-blob blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-6 py-20">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="font-display text-3xl font-medium">Ngawilyi <span className="italic text-ochre">Narrative</span></p>
          <p className="mt-3 font-display italic text-xl text-cream/80">Healing through story. Strength through connection.</p>
          <p className="mt-6 text-sm leading-relaxed text-cream/60 max-w-md">
            An emerging Aboriginal-owned narrative therapy, mentoring, movement and community organisation,
            walking alongside children, young people, families and carers. Our Aunties are narrative therapists —
            blending therapy, mentoring and cultural connection in one consistent relationship.
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.2em] text-ochre font-bold">Your story belongs to you.</p>
        </div>
        <div className="lg:col-span-2">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-cream/50 mb-5">Explore</p>
          <ul className="space-y-3 text-sm">
            {[["About", "/about"], ["The Aunties", "/aunties"], ...(SHOW_GOVERNANCE ? [["Cultural Governance", "/cultural-governance"]] : []), ["Our Approach", "/approach"], ["Programs", "/programs"], ["Threads", "/threads"], ["Culture", "/culture"]].map(([l, to]) => (
              <li key={to}><Link data-testid={`footer-${l.toLowerCase().replace(/\s+/g, "-")}-link`} to={to} className="text-cream/75 hover:text-ochre transition-colors">{l}</Link></li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-2">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-cream/50 mb-5">Connect</p>
          <ul className="space-y-3 text-sm">
            {[["Request Support", "/request-support"], ["Private Supports", "/private-supports"], ["NDIS Supports", "/ndis-supports"], ["Pricing & Fees", "/pricing"], ["Make a Referral", "/referrals"], ["Help Shape Ngawilyi", "/consultation"], ["Partner With Us", "/partner"], ["Privacy & Consent", "/privacy"], ["Contact", "/contact"]].map(([l, to]) => (
              <li key={to}><Link data-testid={`footer-${l.toLowerCase().replace(/\s+/g, "-")}-link`} to={to} className="text-cream/75 hover:text-ochre transition-colors">{l}</Link></li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-3">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-cream/50 mb-5">Contact</p>
          <p className="text-sm text-cream/75">Ngawilyi.project@outlook.com</p>
          <p className="text-sm text-cream/75 mt-2">South Australia</p>
          <p className="mt-6 text-xs leading-relaxed text-cream/50">
            Ngawilyi Narrative is an emerging organisation. Programs are being shaped through community
            consultation. Expressions of interest and pilot partnerships are now open.
          </p>
        </div>
      </div>
      <div className="mt-16 pt-8 border-t border-cream/15">
        <p className="text-xs leading-relaxed text-cream/55 max-w-4xl" data-testid="cultural-acknowledgement">
          Ngawilyi means "Aunty" in Arabana language. The use of Arabana language and authorisation for this
          name and program has been provided through Arabana Elders and the Arabana Aboriginal Corporation.
          We acknowledge the Arabana people and all Aboriginal and Torres Strait Islander peoples as the
          Traditional Owners of the lands on which we live, work and yarn, and pay respect to Elders past
          and present. Ngawilyi Narrative does not claim cultural authority for other Aboriginal Nations.
        </p>
        <p className="mt-6 text-xs text-cream/40">© {new Date().getFullYear()} Ngawilyi Narrative. Privacy-first. Consent-based.</p>
      </div>
    </div>
  </footer>
);

export const Layout = ({ children }) => (
  <div className="min-h-screen bg-cream">
    <Navbar />
    <main>{children}</main>
    <Footer />
    <SearchOverlay />
  </div>
);
